<template>
  <div>
    <slot name="card-header"></slot>
    <slot name="header"></slot>

    <!-- 에디터 모드 선택 -->
    <div class="editor-mode-toggle">
      <button
        type="button"
        :class="['mode-btn', { active: editorMode === 'text' }]"
        @click="onModeChange('text')"
      >
        텍스트 모드
      </button>
      <button
        type="button"
        :class="['mode-btn', { active: editorMode === 'wysiwyg' }]"
        @click="onModeChange('wysiwyg')"
      >
        고급 편집 (표/그림)
      </button>
      <button
        type="button"
        :class="['mode-btn', { active: editorMode === 'html' }]"
        @click="onModeChange('html')"
      >
        HTML 코드
      </button>
    </div>

    <!-- 텍스트 모드 (논문 형식 변환) -->
    <submission-text-editor
      v-if="editorMode === 'text'"
      ref="textEditor"
      :value="rawText"
      @input="handleTextEditorChange"
      @change="handleTextEditorChange"
      @update-content="handleTextEditorChange"
    ></submission-text-editor>

    <!-- HTML 코드 모드 -->
    <div v-else-if="editorMode === 'html'" class="html-editor-container">
      <textarea
        class="html-editor"
        v-model="htmlCode"
        @input="handleHtmlChange"
        spellcheck="false"
      ></textarea>
    </div>

    <!-- 고급 편집 모드 (Jodit) -->
    <custom-jodit-editor
      v-else
      ref="joditEditor"
      :value="content"
      @input="handleEditorChange"
      @change="handleEditorChange"
      :config="editorConfig"
    ></custom-jodit-editor>

    <!-- 주석 목록 영역 -->
    <div v-if="!hideFootnoteList" class="footnote-list-area">
      <div class="footnote-list-header">
        <span class="footnote-list-title">주석 목록</span>
        <span v-if="comments && comments.length > 0" class="footnote-count"
          >({{ comments.length }}개)</span
        >
      </div>
      <div class="footnote-list-content">
        <div v-if="!comments || comments.length === 0" class="footnote-empty">
          등록된 주석이 없습니다.
        </div>
        <div
          v-for="(comment, index) in comments"
          :key="index"
          class="footnote-item"
        >
          <sup class="footnote-number">{{
            comment.superscript || index + 1
          }}</sup>
          <span class="footnote-text">{{ comment.content }}</span>
          <span v-if="comment.page" class="footnote-page"
            >(p.{{ comment.page }})</span
          >
        </div>
      </div>
    </div>

    <slot name="bottom-content"></slot>

    <slot name="bottom-buttons"></slot>
  </div>
</template>

<script>
import CustomJoditEditor from "@/components/editors/CustomJoditEditor.vue";
import SubmissionTextEditor from "@/components/editors/SubmissionTextEditor.vue";

const BASE_API_URL = `${process.env.VUE_APP_SERVER_URI}`;

export default {
  name: "submission-editor-card",
  components: {
    CustomJoditEditor,
    SubmissionTextEditor,
  },
  props: {
    value: {
      type: String,
      default: "",
    },
    rawTextValue: {
      type: String,
      default: "",
    },
    height: {
      type: Number,
      default: 500,
    },
    comments: {
      type: Array,
      default: () => [],
    },
    hideFootnoteList: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    // 기존 HTML 콘텐츠가 있고 rawText가 없으면 WYSIWYG 모드로 시작
    const hasHtmlContent = this.value && this.value.trim().length > 0;
    const hasRawText = this.rawTextValue && this.rawTextValue.trim().length > 0;
    const initialMode = hasHtmlContent && !hasRawText ? "wysiwyg" : "text";

    return {
      content: this.value || "",
      // rawTextValue가 없으면 value를 rawText로도 사용
      rawText: this.rawTextValue || this.value || "",
      htmlCode: this.value || "",
      editorMode: initialMode,
      footnoteDialog: false,
      footnoteInput: "",
      editorConfig: {
        height: this.height,
        enter: "P",
        indentMargin: 20,
        indentBlockMargin: 20,
        tabSize: 4,
        uploader: {
          insertImageAsBase64URI: true,
          enableDragAndDropFileToEditor: true,
          withCredentials: true,
          filesVariableName: function (r) {
            return "file";
          },
          headers: {
            "Content-Type": "multipart/form-data",
          },
          format: "form",
          url: `${BASE_API_URL}/iskal/api/file/static/image`,
          method: "POST",
          prepareData: (formData) => {
            return formData;
          },
          headers: {
            Authorization: `Bearer ${this.$store.getters["auth/authToken"]}`,
          },
          process: (resp) => {
            const baseurl = `${BASE_API_URL}/static/`;
            const imageUrl = `${baseurl}${resp.path}`;

            // 서버에서 이미지 URL 반환 받았을 때 처리
            return {
              files: [imageUrl],
            };
          },
          error: (e) => {
            // eslint-disable-next-line no-console
            console.error("업로드 실패:", e);
          },
        },
        buttons: [
          {
            name: "paragraph",
            list: {
              h1: "대제목 1",
              h2: "대제목 2",
              h3: "중제목 1",
              h4: "중제목 2",
              h5: "소제목 1",
              h6: "소제목 2",
              p: "본문",
            },
            tooltip: "문단 스타일",
          },
          "|",
          {
            name: "first-line-indent",
            icon: "indent",
            tooltip: "첫 줄 들여쓰기",
            exec: (editor) => {
              const sel = editor.selection;
              let current = sel.current();

              if (!current) return;

              // TextNode일 경우 parentElement로 올라감
              if (current.nodeType === Node.TEXT_NODE) {
                current = current.parentElement;
              }

              // 가장 가까운 <p> 태그 찾기
              const pTag = current.closest && current.closest("p");

              if (pTag) {
                if (pTag.style.textIndent === "2em") {
                  pTag.style.textIndent = ""; // 토글 해제
                } else {
                  pTag.style.textIndent = "2em"; // 들여쓰기 적용
                }
              }
            },
          },
          "|",
          {
            name: "font",
            list: {
              "Noto Sans KR, KoPubWorld, sans-serif": "Noto Sans KR",
              "Noto Serif KR, Noto Serif, serif": "Noto Serif KR",
              "KoPubWorldBatang, Noto Sans KR, sans-serif": "KoPubWorld 바탕",
              "KoPubWorldDotum, Noto Sans KR, sans-serif": "KoPubWorld 돋움",
            },
            tooltip: "서체",
          },
          "|",
          "bold",
          "italic",
          "underline",
          "strikethrough",
          "|",
          "superscript",
          "subscript",
          "|",
          {
            name: "위첨자",
            icon: "위첨자",
            tooltip: "위첨자 (자동 번호)",
            exec: (editor) => {
              const selection = editor.selection;
              const range = selection.range;

              if (!range.collapsed) {
                // 선택된 텍스트 뒤에 자동 번호 첨자 추가
                const selectedText = range.extractContents().textContent;
                const textNode = document.createTextNode(selectedText);
                range.insertNode(textNode);

                // 다음 번호 계산
                const nextNumber = this.getNextSuperscriptNumber(editor);
                const sup = editor.createInside.element("sup");
                sup.textContent = nextNumber.toString();
                sup.setAttribute("data-auto-number", "true");

                // 선택된 텍스트 뒤에 첨자 삽입
                range.setStartAfter(textNode);
                range.setEndAfter(textNode);
                range.insertNode(sup);

                // 첨자 뒤로 커서 이동
                range.setStartAfter(sup);
                range.setEndAfter(sup);
                selection.selectRange(range);
              } else {
                // 커서 위치에 자동 번호 첨자 추가
                const nextNumber = this.getNextSuperscriptNumber(editor);
                const sup = editor.createInside.element("sup");
                sup.textContent = nextNumber.toString();
                sup.setAttribute("data-auto-number", "true");
                range.insertNode(sup);

                // 첨자 뒤로 커서 이동
                range.setStartAfter(sup);
                range.setEndAfter(sup);
                selection.selectRange(range);
              }

              // 번호 재정렬
              this.renumberSuperscripts(editor);
            },
          },
          {
            name: "아래첨자",
            icon: "아래첨자",
            tooltip: "아래첨자 (자동 번호)",
            exec: (editor) => {
              const selection = editor.selection;
              const range = selection.range;

              if (!range.collapsed) {
                // 선택된 텍스트 뒤에 자동 번호 첨자 추가
                const selectedText = range.extractContents().textContent;
                const textNode = document.createTextNode(selectedText);
                range.insertNode(textNode);

                // 다음 번호 계산
                const nextNumber = this.getNextSubscriptNumber(editor);
                const sub = editor.createInside.element("sub");
                sub.textContent = nextNumber.toString();
                sub.setAttribute("data-auto-number", "true");

                // 선택된 텍스트 뒤에 첨자 삽입
                range.setStartAfter(textNode);
                range.setEndAfter(textNode);
                range.insertNode(sub);

                // 첨자 뒤로 커서 이동
                range.setStartAfter(sub);
                range.setEndAfter(sub);
                selection.selectRange(range);
              } else {
                // 커서 위치에 자동 번호 첨자 추가
                const nextNumber = this.getNextSubscriptNumber(editor);
                const sub = editor.createInside.element("sub");
                sub.textContent = nextNumber.toString();
                sub.setAttribute("data-auto-number", "true");
                range.insertNode(sub);

                // 첨자 뒤로 커서 이동
                range.setStartAfter(sub);
                range.setEndAfter(sub);
                selection.selectRange(range);
              }

              // 번호 재정렬
              this.renumberSubscripts(editor);
            },
          },
          {
            name: "주석",
            icon: "📝",
            tooltip: "주석 추가",
            popup: (editor) => {
              const wrapper = editor.create.div();
              wrapper.style.padding = "10px";
              wrapper.style.minWidth = "320px";

              // 스타일 공통
              const labelStyle =
                "display: block; margin-top: 10px; font-size: 12px; color: #333; font-weight: bold;";
              const inputStyle =
                "width: 100%; margin-top: 4px; padding: 8px; border: 1px solid #ddd; border-radius: 4px; font-size: 13px; box-sizing: border-box;";

              // 제목
              const title = document.createElement("div");
              title.innerText = "주석 추가";
              title.style.cssText =
                "font-size: 14px; font-weight: bold; margin-bottom: 10px; color: #333;";

              // 다음 주석 번호 계산 (comments 배열 기반)
              const nextNumber = (this.comments?.length || 0) + 1;

              // 첨자 번호 표시
              const superscriptWrapper = document.createElement("div");
              superscriptWrapper.style.cssText = "margin-top: 6px;";

              const superscriptLabel = document.createElement("label");
              superscriptLabel.innerText = "첨자 번호 (자동)";
              superscriptLabel.setAttribute("style", labelStyle);
              superscriptLabel.style.marginTop = "0";

              const superscriptInput = document.createElement("input");
              superscriptInput.type = "text";
              superscriptInput.value = `${nextNumber})`;
              superscriptInput.setAttribute("style", inputStyle);
              superscriptInput.style.backgroundColor = "#f5f5f5";
              superscriptInput.style.color = "#1976d2";
              superscriptInput.style.fontWeight = "bold";
              superscriptInput.style.width = "80px";
              superscriptInput.readOnly = true;

              superscriptWrapper.appendChild(superscriptLabel);
              superscriptWrapper.appendChild(superscriptInput);

              // 안내 문구
              const hint = document.createElement("div");
              hint.innerText = "주석이 삽입된 위치의 페이지 하단에 자동으로 표시됩니다.";
              hint.style.cssText = "font-size: 11px; color: #666; margin-top: 8px; padding: 8px; background: #f5f5f5; border-radius: 4px;";

              // 주석 내용 입력
              const contentLabel = document.createElement("label");
              contentLabel.innerText = "내용";
              contentLabel.setAttribute("style", labelStyle);

              const contentInput = document.createElement("textarea");
              contentInput.placeholder = "주석 내용을 입력하세요...";
              contentInput.setAttribute("style", inputStyle);
              contentInput.style.height = "80px";
              contentInput.style.resize = "vertical";

              // 버튼 영역
              const btnWrapper = document.createElement("div");
              btnWrapper.style.cssText =
                "display: flex; justify-content: flex-end; gap: 8px; margin-top: 12px;";

              // 취소 버튼
              const cancelBtn = document.createElement("button");
              cancelBtn.textContent = "취소";
              cancelBtn.style.cssText =
                "padding: 6px 12px; border: 1px solid #ddd; background: #fff; border-radius: 4px; cursor: pointer;";
              cancelBtn.onclick = () => {
                editor.events.fire("hidePopup");
              };

              // 삽입 버튼
              const insertBtn = document.createElement("button");
              insertBtn.textContent = "삽입";
              insertBtn.style.cssText =
                "padding: 6px 12px; border: none; background: #1976d2; color: white; border-radius: 4px; cursor: pointer;";

              insertBtn.onclick = () => {
                const noteContent = contentInput.value.trim();
                if (!noteContent) {
                  alert("주석 내용을 입력해주세요.");
                  return;
                }

                // 본문에 위첨자 번호 삽입 (data-footnote-id 속성 포함)
                const sup = editor.createInside.element("sup");
                sup.textContent = `${nextNumber})`;
                sup.setAttribute("data-footnote-id", nextNumber.toString());
                sup.style.cssText =
                  "color: #1976d2; cursor: pointer; font-weight: bold;";
                sup.title = noteContent;

                editor.s.insertNode(sup);

                // 부모 컴포넌트에 주석 추가 이벤트 emit (페이지는 자동 계산되므로 제외)
                this.$emit("add-comment", {
                  superscript: `${nextNumber})`,
                  content: noteContent,
                });

                editor.events.fire("hidePopup");
              };

              // 구성
              wrapper.appendChild(title);
              wrapper.appendChild(superscriptWrapper);
              wrapper.appendChild(hint);
              wrapper.appendChild(contentLabel);
              wrapper.appendChild(contentInput);
              btnWrapper.appendChild(cancelBtn);
              btnWrapper.appendChild(insertBtn);
              wrapper.appendChild(btnWrapper);

              // 포커스 설정
              setTimeout(() => contentInput.focus(), 100);

              return wrapper;
            },
          },
          "|",
          "ul",
          "ol",
          "|",
          "table",
          {
            name: "열균등분배",
            icon: "열균등분배",
            tooltip: "선택된 셀들의 열 균등 분배",
            exec: (editor) => {
              this.distributeSelectedCellsWidth(editor);
            },
          },
          {
            name: "행균등분배",
            icon: "행균등분배",
            tooltip: "선택된 셀들의 행 균등 분배",
            exec: (editor) => {
              this.distributeSelectedCellsHeight(editor);
            },
          },
          {
            name: "ⓒ",
            tooltip: "캡션 추가",
            exec: (editor) => {
              let currentElement = editor.selection.current();

              // 텍스트 노드인 경우 부모 요소로 이동
              if (
                currentElement &&
                currentElement.nodeType === Node.TEXT_NODE
              ) {
                currentElement = currentElement.parentElement;
              }

              // 이미지 또는 테이블 찾기
              const table = currentElement
                ? currentElement.closest("table")
                : null;
              const image =
                currentElement && currentElement.tagName === "IMG"
                  ? currentElement
                  : null;

              if (table) {
                // 기존 캡션 제거
                const oldCaption = table.querySelector("caption");
                if (oldCaption) {
                  oldCaption.remove();
                }

                const caption = editor.create.element("caption");
                caption.textContent = "표. 여기에 설명을 입력하세요";
                table.insertBefore(caption, table.firstChild);
              } else if (image) {
                // 이미지에 캡션 추가
                const figure = editor.create.element("figure");
                const figcaption = editor.create.element("figcaption");
                figcaption.textContent = "이미지. 여기에 설명을 입력하세요";

                image.parentNode.insertBefore(figure, image);
                figure.appendChild(figcaption);
                figure.appendChild(image);

                // 스타일 적용
                figure.style.display = "inline-block";
                figure.style.margin = "0";
                figcaption.style.textAlign = "center";
              } else {
                editor.alert(
                  "커서가 표나 이미지 안에 있어야 캡션을 추가할 수 있습니다."
                );
              }
            },
          },
          "|",
          "image",
          "|",
          "align",
          "|",
          {
            name: "수평선",
            icon: "hr",
            popup: (editor) => {
              const wrapper = editor.create.div();

              // 스타일 공통
              const labelStyle =
                "display: block; margin-top: 6px; font-size: 12px; color: #333;";
              const inputStyle = "width: 100%; margin-top: 2px;";

              // 선 색상
              const colorLabel = document.createElement("label");
              colorLabel.innerText = "선 색상";
              colorLabel.setAttribute("style", labelStyle);
              const colorInput = document.createElement("input");
              colorInput.type = "color";
              colorInput.value = "#000000";
              colorInput.setAttribute("style", inputStyle);

              // 너비
              const widthLabel = document.createElement("label");
              widthLabel.innerText = "너비 (%)";
              widthLabel.setAttribute("style", labelStyle);
              const widthInput = document.createElement("input");
              widthInput.type = "range";
              widthInput.min = 10;
              widthInput.max = 100;
              widthInput.value = 100;
              widthInput.setAttribute("style", inputStyle);

              // 두께
              const heightLabel = document.createElement("label");
              heightLabel.innerText = "두께 (px)";
              heightLabel.setAttribute("style", labelStyle);
              const heightInput = document.createElement("input");
              heightInput.type = "range";
              heightInput.min = 0.5;
              heightInput.max = 10;
              heightInput.step = 0.1;
              heightInput.value = 1;
              heightInput.setAttribute("style", inputStyle);

              // 삽입 버튼
              const insertBtn = document.createElement("button");
              insertBtn.textContent = "삽입";
              insertBtn.style.marginTop = "10px";

              insertBtn.onclick = () => {
                const color = colorInput.value;
                const width = widthInput.value + "%";
                const height = heightInput.value + "px";
                editor.s.insertHTML(
                  `<hr style="border: none; border-top: ${height} solid ${color}; width: ${width};" />`
                );
                editor.events.fire("hidePopup");
              };

              // 구성
              wrapper.appendChild(colorLabel);
              wrapper.appendChild(colorInput);

              wrapper.appendChild(widthLabel);
              wrapper.appendChild(widthInput);

              wrapper.appendChild(heightLabel);
              wrapper.appendChild(heightInput);

              wrapper.appendChild(insertBtn);

              return wrapper;
            },
          },
          "|",
          "source",
          "|",
          "undo",
          "redo",
          "|",
        ],
        defaultActionOnPaste: "insert_clear_html",
        toolbarAdaptive: false,
        askBeforePasteHTML: false,
        allowResizeX: true,
        allowResizeY: true,
        useImageEditor: true,
        createAttributes: {
          table: {
            style: "border:1px solid;border-collapse:collapse;width: 100%;",
          },
          h1: {
            style:
              "font-size: 20px !important; font-weight: 900 !important; line-height: 27px !important; letter-spacing: -0.5px !important;",
          },
          h2: {
            style:
              "font-size: 18px !important; font-weight: 800 !important; line-height: 25px !important; letter-spacing: -0.5px !important;",
          },
          h3: {
            style:
              "font-size: 16px; font-weight: 700; !important; line-height: 21px !important; letter-spacing: -0.5px !important;",
          },
          h4: {
            style:
              "font-size: 14px; font-weight: 700; !important; line-height: 17px !important; letter-spacing: -0.5px !important;",
          },
          h5: {
            style:
              "font-size: 12px; font-weight: 600; !important; line-height: 15px !important; letter-spacing: -0.5px !important;",
          },
          h6: {
            style:
              "font-size: 10px; font-weight: 600; !important; line-height: 13px !important; letter-spacing: -0.5px !important;",
          },
          tr: {
            style: " border: 0.5px solid; border-collapse: collapse;",
          },
          td: {
            style: " border: 1px solid; border-collapse: collapse;",
          },
        },
      },
    };
  },
  created() {
    // rawTextValue가 없고 value(HTML)만 있는 경우, HTML을 텍스트 형식으로 변환
    // 특히 blockquote의 <br> 태그를 줄바꿈으로 보존
    if (!this.rawTextValue && this.value && this.value.includes('<')) {
      this.rawText = this.htmlToTextFormat(this.value);
    }
  },
  mounted() {
    this.addSuperscriptDeletionListener();
    this.addTableCellSelectionListener();
  },
  methods: {
    handleEditorChange(newContent) {
      this.content = newContent || "";
      this.$emit("update-content", this.content);
      this.$emit("input", this.content);
    },

    handleTextEditorChange(htmlContent) {
      this.content = htmlContent || "";
      this.$emit("update-content", this.content);
      this.$emit("input", this.content);
    },

    // HTML을 텍스트 모드용 형식으로 변환 (blockquote의 <br> 태그를 줄바꿈으로 보존)
    htmlToTextFormat(html) {
      if (!html) return "";

      const div = document.createElement("div");
      div.innerHTML = html;
      const lines = [];

      const processNode = (node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          const text = node.textContent.trim();
          if (text) lines.push(text);
          return;
        }

        if (node.nodeType !== Node.ELEMENT_NODE) return;

        const tag = node.tagName.toLowerCase();

        // blockquote 처리 - <br> 태그를 줄바꿈으로 변환하여 각 줄에 > 추가
        if (tag === "blockquote" || node.classList.contains("blockquote") || node.classList.contains("doc-blockquote")) {
          // <br> 태그를 줄바꿈 문자로 변환
          const htmlContent = node.innerHTML.replace(/<br\s*\/?>/gi, '\n');
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = htmlContent;
          const textContent = tempDiv.textContent.trim();

          // 줄바꿈으로 분리하여 각 줄에 > 추가
          textContent.split("\n").forEach(line => {
            if (line.trim()) {
              lines.push("> " + line.trim());
            }
          });
          lines.push(""); // 빈 줄 추가
          return;
        }

        // 헤딩 처리
        if (/^h[1-6]$/.test(tag)) {
          const level = parseInt(tag[1]);
          const prefix = "#".repeat(level) + " ";
          lines.push(prefix + node.textContent.trim());
          lines.push("");
          return;
        }

        // 테이블 처리
        if (tag === "table") {
          const caption = node.querySelector("caption");
          if (caption) {
            const captionText = caption.textContent.trim();
            const captionMatch = captionText.match(/^<?\s*표\s*(\d+)\s*>?\s*(.*)$/);
            if (captionMatch) {
              lines.push(`<표 ${captionMatch[1]}> ${captionMatch[2] || ''}`);
            } else {
              lines.push(`<표 1> ${captionText}`);
            }
          }
          node.querySelectorAll("tr").forEach((row, rowIndex) => {
            const cells = Array.from(row.querySelectorAll("th, td"));
            const cellTexts = cells.map(cell => cell.textContent.trim());
            lines.push("| " + cellTexts.join(" | ") + " |");

            // 첫 번째 행 다음에 구분선 추가 (헤더)
            if (rowIndex === 0) {
              lines.push("| " + cells.map(() => "---").join(" | ") + " |");
            }
          });
          lines.push("");
          return;
        }

        // 리스트 처리
        if (tag === "ul" || tag === "ol") {
          node.querySelectorAll("li").forEach((li, index) => {
            const prefix = tag === "ol" ? `${index + 1}. ` : "- ";
            lines.push(prefix + li.textContent.trim());
          });
          lines.push("");
          return;
        }

        // 단락 처리
        if (tag === "p") {
          const text = node.textContent.trim();
          if (text) {
            lines.push(text);
            lines.push("");
          }
          return;
        }

        // 그 외 요소들은 자식 노드 순회
        node.childNodes.forEach(child => processNode(child));
      };

      div.childNodes.forEach(child => processNode(child));

      // 연속된 빈 줄 정리
      return lines.join("\n").replace(/\n{3,}/g, "\n\n").trim();
    },

    // HTML을 텍스트로 변환 (텍스트 모드 전환 시 사용)
    htmlToPlainText(html) {
      if (!html) return "";

      // 임시 div에 HTML 파싱
      const div = document.createElement("div");
      div.innerHTML = html;

      // 헤딩 변환
      div.querySelectorAll("h1, h2, h3, h4, h5, h6").forEach((el) => {
        const text = el.textContent.trim();
        el.replaceWith(document.createTextNode(text + "\n\n"));
      });

      // 단락 변환
      div.querySelectorAll("p").forEach((el) => {
        const text = el.textContent.trim();
        el.replaceWith(document.createTextNode(text + "\n\n"));
      });

      // 리스트 변환
      div.querySelectorAll("li").forEach((el) => {
        const text = el.textContent.trim();
        el.replaceWith(document.createTextNode("• " + text + "\n"));
      });
      div.querySelectorAll("ul, ol").forEach((el) => {
        el.replaceWith(document.createTextNode("\n"));
      });

      // 테이블 유지 (간단히 텍스트로)
      div.querySelectorAll("table").forEach((table) => {
        const caption = table.querySelector("caption");
        let tableText = "";
        if (caption) {
          tableText += caption.textContent.trim() + "\n";
        }
        table.querySelectorAll("tr").forEach((row) => {
          const cells = Array.from(row.querySelectorAll("th, td"))
            .map((cell) => cell.textContent.trim())
            .join(" | ");
          tableText += "| " + cells + " |\n";
        });
        table.replaceWith(document.createTextNode(tableText + "\n"));
      });

      // 나머지 태그 제거
      let text = div.textContent || div.innerText;

      // 여러 줄바꿈 정리
      text = text.replace(/\n{3,}/g, "\n\n").trim();

      return text;
    },

    // 모드 전환 시 콘텐츠 변환
    onModeChange(newMode) {
      if (newMode === "text" && !this.rawText && this.content) {
        // WYSIWYG에서 텍스트 모드로 전환 시 HTML을 텍스트로 변환
        this.rawText = this.htmlToPlainText(this.content);
      }
      if (newMode === "html") {
        // HTML 모드로 전환 시 현재 content를 htmlCode에 동기화
        this.htmlCode = this.content || "";
      }
      if (this.editorMode === "html" && newMode !== "html") {
        // HTML 모드에서 다른 모드로 전환 시 htmlCode를 content에 동기화
        this.content = this.htmlCode || "";
      }
      this.editorMode = newMode;
    },

    handleHtmlChange() {
      // HTML 코드 직접 편집 시 content에 반영
      this.content = this.htmlCode || "";
      this.$emit("update-content", this.content);
      this.$emit("input", this.content);
    },

    // 위첨자 다음 번호 계산
    getNextSuperscriptNumber(editor) {
      const existingSups = editor.editor.querySelectorAll(
        "sup[data-auto-number]"
      );
      const numbers = Array.from(existingSups).map(
        (sup) => parseInt(sup.textContent) || 0
      );
      return numbers.length > 0 ? Math.max(...numbers) + 1 : 1;
    },

    // 아래첨자 다음 번호 계산
    getNextSubscriptNumber(editor) {
      const existingSubs = editor.editor.querySelectorAll(
        "sub[data-auto-number]"
      );
      const numbers = Array.from(existingSubs).map(
        (sub) => parseInt(sub.textContent) || 0
      );
      return numbers.length > 0 ? Math.max(...numbers) + 1 : 1;
    },

    // 위첨자 번호 재정렬
    renumberSuperscripts(editor) {
      const sups = editor.editor.querySelectorAll("sup[data-auto-number]");
      let number = 1;
      sups.forEach((sup) => {
        sup.textContent = number.toString();
        number++;
      });
    },

    // 아래첨자 번호 재정렬
    renumberSubscripts(editor) {
      const subs = editor.editor.querySelectorAll("sub[data-auto-number]");
      let number = 1;
      subs.forEach((sub) => {
        sub.textContent = number.toString();
        number++;
      });
    },

    // 첨자 삭제 감지 및 번호 재정렬
    addSuperscriptDeletionListener() {
      const editor = this.$refs.editor?.editor || this.$children[0].editor;
      if (!editor) return;

      // MutationObserver를 사용하여 DOM 변경 감지
      const observer = new MutationObserver((mutations) => {
        let shouldRenumber = false;

        mutations.forEach((mutation) => {
          if (mutation.type === "childList") {
            // 삭제된 노드들 확인
            mutation.removedNodes.forEach((node) => {
              if (node.nodeType === Node.ELEMENT_NODE) {
                const deletedSups = node.querySelectorAll
                  ? node.querySelectorAll(
                      "sup[data-auto-number], sub[data-auto-number]"
                    )
                  : [];
                const isDeletedSup =
                  node.tagName === "SUP" &&
                  node.getAttribute("data-auto-number");
                const isDeletedSub =
                  node.tagName === "SUB" &&
                  node.getAttribute("data-auto-number");

                if (deletedSups.length > 0 || isDeletedSup || isDeletedSub) {
                  shouldRenumber = true;
                }
              }
            });
          }
        });

        if (shouldRenumber) {
          // 약간의 지연을 두고 재정렬 (DOM 업데이트 완료 후)
          setTimeout(() => {
            this.renumberSuperscripts(editor);
            this.renumberSubscripts(editor);
          }, 10);
        }
      });

      // 에디터 내용 변경 감지
      observer.observe(editor.editor, {
        childList: true,
        subtree: true,
      });

      // 에디터 인스턴스에 observer 저장 (나중에 정리할 수 있도록)
      editor.__superscriptObserver = observer;
    },

    // 테이블 셀 선택 리스너 추가
    addTableCellSelectionListener() {
      this.$nextTick(() => {
        const editor = this.$refs.editor?.editor || this.$children[0]?.editor;
        if (!editor) return;

        // 선택된 셀들을 저장할 변수
        editor.__selectedCells = [];

        // 에디터에 클릭 이벤트 리스너 추가
        const handleCellClick = (e) => {
          const cell = e.target.closest("td, th");
          if (cell) {
            // Ctrl/Cmd 키가 눌린 경우 다중 선택
            if (e.ctrlKey || e.metaKey) {
              if (editor.__selectedCells.includes(cell)) {
                // 이미 선택된 셀인 경우 선택 해제
                editor.__selectedCells = editor.__selectedCells.filter(
                  (c) => c !== cell
                );
                cell.style.backgroundColor = "";
                cell.style.border = "";
              } else {
                // 새로 선택
                editor.__selectedCells.push(cell);
                cell.style.backgroundColor = "rgba(0, 123, 255, 0.3)";
                cell.style.border = "2px solid #007bff";
              }
            } else {
              // 단일 선택 - 기존 선택 모두 해제
              editor.__selectedCells.forEach((c) => {
                c.style.backgroundColor = "";
                c.style.border = "";
              });
              editor.__selectedCells = [cell];
              cell.style.backgroundColor = "rgba(0, 123, 255, 0.3)";
              cell.style.border = "2px solid #007bff";
            }

            e.preventDefault();
            e.stopPropagation();
          }
        };

        // 에디터에 드래그 선택 이벤트 리스너 추가
        const handleMouseDown = (e) => {
          const cell = e.target.closest("td, th");
          if (cell) {
            editor.__dragStartCell = cell;
            editor.__isDragging = false;
          }
        };

        const handleMouseMove = (e) => {
          if (editor.__dragStartCell) {
            editor.__isDragging = true;
            const currentCell = e.target.closest("td, th");
            if (currentCell && currentCell !== editor.__dragStartCell) {
              // 드래그 범위 선택 로직
              this.selectCellRange(editor, editor.__dragStartCell, currentCell);
            }
          }
        };

        const handleMouseUp = (e) => {
          if (editor.__dragStartCell && !editor.__isDragging) {
            // 클릭만 한 경우 (드래그 아님)
            handleCellClick(e);
          }
          editor.__dragStartCell = null;
          editor.__isDragging = false;
        };

        // 이벤트 리스너 등록
        editor.editor.addEventListener("mousedown", handleMouseDown);
        editor.editor.addEventListener("mousemove", handleMouseMove);
        editor.editor.addEventListener("mouseup", handleMouseUp);
        editor.editor.addEventListener("click", handleCellClick);

        // 에디터 인스턴스에 리스너 저장 (나중에 정리할 수 있도록)
        editor.__cellSelectionListeners = {
          mousedown: handleMouseDown,
          mousemove: handleMouseMove,
          mouseup: handleMouseUp,
          click: handleCellClick,
        };
      });
    },

    // 셀 범위 선택
    selectCellRange(editor, startCell, endCell) {
      const table = startCell.closest("table");
      if (!table) return;

      const allCells = Array.from(table.querySelectorAll("td, th"));
      const startIndex = allCells.indexOf(startCell);
      const endIndex = allCells.indexOf(endCell);

      if (startIndex === -1 || endIndex === -1) return;

      // 기존 선택 모두 해제
      editor.__selectedCells.forEach((c) => {
        c.style.backgroundColor = "";
        c.style.border = "";
      });

      // 범위 내 셀들 선택
      editor.__selectedCells = [];
      const minIndex = Math.min(startIndex, endIndex);
      const maxIndex = Math.max(startIndex, endIndex);

      for (let i = minIndex; i <= maxIndex; i++) {
        const cell = allCells[i];
        editor.__selectedCells.push(cell);
        cell.style.backgroundColor = "rgba(0, 123, 255, 0.3)";
        cell.style.border = "2px solid #007bff";
      }
    },

    // 선택된 테이블 셀들 가져오기
    getSelectedTableCells(editor) {
      const selection = editor.selection;
      const range = selection.range;

      if (!range || range.collapsed) {
        // 범위가 없거나 축소된 경우, 현재 커서 위치의 셀 확인
        const currentCell = this.findTableCell(selection.current());
        return currentCell ? [currentCell] : [];
      }

      const selectedCells = [];

      // 방법 1: 선택 범위에서 직접 셀들 찾기
      const rangeContents = range.cloneContents();
      const cellsInRange = rangeContents.querySelectorAll("td, th");

      if (cellsInRange.length > 0) {
        // 선택 범위 내의 셀들을 실제 DOM에서 찾기
        cellsInRange.forEach((rangeCell) => {
          // rangeCell은 DocumentFragment 내의 요소이므로 실제 DOM에서 찾아야 함
          const cellText = rangeCell.textContent;
          const table = this.findTableInEditor(editor);
          if (table) {
            const actualCells = table.querySelectorAll("td, th");
            actualCells.forEach((actualCell) => {
              if (
                actualCell.textContent === cellText &&
                !selectedCells.includes(actualCell)
              ) {
                selectedCells.push(actualCell);
              }
            });
          }
        });
      }

      // 방법 2: 시작/끝 컨테이너에서 셀 찾기
      if (selectedCells.length === 0) {
        const startContainer = range.startContainer;
        const endContainer = range.endContainer;

        const startCell = this.findTableCell(startContainer);
        const endCell = this.findTableCell(endContainer);

        if (startCell && endCell) {
          const table = startCell.closest("table");
          if (table && table === endCell.closest("table")) {
            const allCells = Array.from(table.querySelectorAll("td, th"));
            const startIndex = allCells.indexOf(startCell);
            const endIndex = allCells.indexOf(endCell);

            if (startIndex !== -1 && endIndex !== -1) {
              const minIndex = Math.min(startIndex, endIndex);
              const maxIndex = Math.max(startIndex, endIndex);

              for (let i = minIndex; i <= maxIndex; i++) {
                selectedCells.push(allCells[i]);
              }
            }
          }
        }
      }

      // 방법 3: Jodit의 selection API 사용
      if (selectedCells.length === 0) {
        const selectedElements = selection.getSelectedElements();
        selectedElements.forEach((element) => {
          const cell = this.findTableCell(element);
          if (cell && !selectedCells.includes(cell)) {
            selectedCells.push(cell);
          }
        });
      }

      // 방법 4: 현재 선택된 요소가 테이블 셀인지 확인
      if (selectedCells.length === 0) {
        const currentElement = selection.current();
        const cell = this.findTableCell(currentElement);
        if (cell) {
          selectedCells.push(cell);
        }
      }

      return selectedCells;
    },

    // 에디터에서 테이블 찾기
    findTableInEditor(editor) {
      const editorBody = editor.editor || editor.editor.body;
      return editorBody.querySelector("table");
    },

    // 더 간단한 셀 선택 감지 방법
    getSelectedTableCellsSimple(editor) {
      const editorBody = editor.editor || editor.editor.body;
      const tables = editorBody.querySelectorAll("table");

      if (tables.length === 0) {
        return [];
      }

      const selectedCells = [];

      // 모든 테이블에서 선택된 셀들 찾기
      tables.forEach((table) => {
        const cells = table.querySelectorAll("td, th");

        cells.forEach((cell) => {
          // 셀이 선택되어 있는지 확인하는 여러 방법
          const isSelected = this.isCellSelected(cell, editor);
          if (isSelected) {
            selectedCells.push(cell);
          }
        });
      });

      return selectedCells;
    },

    // Jodit 전용 셀 선택 감지 (가장 정확한 방법)
    getSelectedTableCellsJodit(editor) {
      const selectedCells = [];

      try {
        // 방법 1: Range를 직접 사용한 선택 감지
        const range = editor.selection.range;

        if (range && !range.collapsed) {
          // Range가 있는 경우, 선택 범위 내의 셀들 찾기
          const startContainer = range.startContainer;
          const endContainer = range.endContainer;

          // 시작과 끝 컨테이너에서 셀 찾기
          const startCell = this.findTableCell(startContainer);
          const endCell = this.findTableCell(endContainer);

          if (startCell && endCell) {
            const table = startCell.closest("table");
            if (table && table === endCell.closest("table")) {
              const allCells = Array.from(table.querySelectorAll("td, th"));
              const startIndex = allCells.indexOf(startCell);
              const endIndex = allCells.indexOf(endCell);

              if (startIndex !== -1 && endIndex !== -1) {
                const minIndex = Math.min(startIndex, endIndex);
                const maxIndex = Math.max(startIndex, endIndex);

                for (let i = minIndex; i <= maxIndex; i++) {
                  selectedCells.push(allCells[i]);
                }
              }
            }
          }
        }

        // 방법 2: 에디터 내에서 시각적으로 선택된 셀들 찾기
        if (selectedCells.length === 0) {
          const editorBody = editor.editor || editor.editor.body;
          const allCells = editorBody.querySelectorAll("td, th");

          allCells.forEach((cell) => {
            const computedStyle = window.getComputedStyle(cell);
            const backgroundColor = computedStyle.backgroundColor;
            const borderColor = computedStyle.borderColor;

            // 선택된 셀은 보통 파란색 배경이나 테두리를 가짐
            let isSelected = false;

            // 배경색 확인
            if (
              backgroundColor &&
              backgroundColor !== "rgba(0, 0, 0, 0)" &&
              backgroundColor !== "transparent" &&
              backgroundColor !== "rgb(255, 255, 255)"
            ) {
              const rgbMatch = backgroundColor.match(
                /rgba?\((\d+),\s*(\d+),\s*(\d+)/
              );
              if (rgbMatch) {
                const r = parseInt(rgbMatch[1]);
                const g = parseInt(rgbMatch[2]);
                const b = parseInt(rgbMatch[3]);

                // 파란색 계열인지 확인
                if (b > r && b > g) {
                  isSelected = true;
                }
              }
            }

            // 테두리색 확인 (Jodit에서 선택된 셀은 파란색 테두리를 가질 수 있음)
            if (
              !isSelected &&
              borderColor &&
              borderColor !== "rgba(0, 0, 0, 0)" &&
              borderColor !== "transparent"
            ) {
              const rgbMatch = borderColor.match(
                /rgba?\((\d+),\s*(\d+),\s*(\d+)/
              );
              if (rgbMatch) {
                const r = parseInt(rgbMatch[1]);
                const g = parseInt(rgbMatch[2]);
                const b = parseInt(rgbMatch[3]);

                // 파란색 계열인지 확인
                if (b > r && b > g) {
                  isSelected = true;
                }
              }
            }

            if (isSelected && !selectedCells.includes(cell)) {
              selectedCells.push(cell);
            }
          });
        }

        // 방법 3: 현재 커서 위치의 셀 확인
        if (selectedCells.length === 0) {
          try {
            const currentElement = editor.selection.current();
            if (currentElement) {
              const cell = this.findTableCell(currentElement);
              if (cell) {
                selectedCells.push(cell);
              }
            }
          } catch (e) {}
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
      }

      return selectedCells;
    },

    // 셀이 선택되어 있는지 확인 (Jodit 전용)
    isCellSelected(cell, editor) {
      // 방법 1: Jodit의 선택된 요소들 확인
      try {
        const selectedElements = editor.selection.getSelectedElements();
        if (selectedElements && selectedElements.length > 0) {
          return selectedElements.includes(cell);
        }
      } catch (e) {}

      // 방법 2: 셀에 Jodit 선택 클래스가 있는지 확인
      if (
        cell.classList.contains("jodit-selected-cell") ||
        cell.classList.contains("jodit-cell-selected")
      ) {
        return true;
      }

      // 방법 3: 셀의 부모 요소가 선택 클래스를 가지고 있는지 확인
      let parent = cell.parentNode;
      while (parent && parent !== editor.editor) {
        if (
          parent.classList.contains("jodit-selected-row") ||
          parent.classList.contains("jodit-row-selected") ||
          parent.classList.contains("jodit-selected-cells")
        ) {
          return true;
        }
        parent = parent.parentNode;
      }

      // 방법 4: 셀에 시각적 선택 스타일이 있는지 확인
      const computedStyle = window.getComputedStyle(cell);
      const backgroundColor = computedStyle.backgroundColor;

      // Jodit에서 선택된 셀은 보통 파란색 배경을 가짐
      if (backgroundColor) {
        // 파란색 계열의 배경색인지 확인
        const rgbMatch = backgroundColor.match(
          /rgba?\((\d+),\s*(\d+),\s*(\d+)/
        );
        if (rgbMatch) {
          const r = parseInt(rgbMatch[1]);
          const g = parseInt(rgbMatch[2]);
          const b = parseInt(rgbMatch[3]);

          // 파란색 계열이고 투명하지 않은 경우
          if (
            b > r &&
            b > g &&
            backgroundColor !== "rgba(0, 0, 0, 0)" &&
            backgroundColor !== "transparent" &&
            backgroundColor !== "rgb(255, 255, 255)"
          ) {
            return true;
          }
        }
      }

      // 방법 5: Range를 사용한 확인
      const range = editor.selection.range;
      if (range && !range.collapsed) {
        try {
          // Range가 셀과 교차하는지 확인
          const cellRect = cell.getBoundingClientRect();
          const rangeRect = range.getBoundingClientRect();

          if (cellRect && rangeRect) {
            return !(
              cellRect.right < rangeRect.left ||
              cellRect.left > rangeRect.right ||
              cellRect.bottom < rangeRect.top ||
              cellRect.top > rangeRect.bottom
            );
          }
        } catch (e) {}
      }

      // 방법 6: Jodit의 테이블 모듈 API 사용
      try {
        if (editor.modules && editor.modules.Table) {
          const tableModule = editor.modules.Table;
          const selectedCells =
            tableModule.getSelectedCells && tableModule.getSelectedCells();
          if (selectedCells && selectedCells.includes(cell)) {
            return true;
          }
        }
      } catch (e) {}

      return false;
    },

    // 노드에서 테이블 셀 찾기
    findTableCell(node) {
      if (!node) return null;

      if (
        node.nodeType === Node.ELEMENT_NODE &&
        (node.tagName === "TD" || node.tagName === "TH")
      ) {
        return node;
      }

      return node.closest ? node.closest("td, th") : null;
    },

    // 선택된 셀들의 열 균등 분배
    distributeSelectedCellsWidth(editor) {
      // 먼저 우리가 직접 관리하는 선택된 셀들 확인
      let selectedCells = editor.__selectedCells || [];

      // 직접 관리하는 셀들이 없는 경우 다른 방법들 시도
      if (selectedCells.length === 0) {
        selectedCells = this.getSelectedTableCellsJodit(editor);
      }

      if (selectedCells.length === 0) {
        selectedCells = this.getSelectedTableCellsSimple(editor);
      }

      if (selectedCells.length === 0) {
        selectedCells = this.getSelectedTableCells(editor);
      }

      if (selectedCells.length === 0) {
        editor.alert("표에서 셀을 선택한 후 다시 시도해주세요.");
        return;
      }

      // 선택된 셀들을 행별로 그룹화
      const cellsByRow = {};
      selectedCells.forEach((cell) => {
        const row = cell.parentNode;
        if (!cellsByRow[row]) {
          cellsByRow[row] = [];
        }
        cellsByRow[row].push(cell);
      });

      // 각 행에서 연속된 셀들의 너비 균등 분배
      Object.values(cellsByRow).forEach((rowCells) => {
        if (rowCells.length <= 1) return;

        // 연속된 셀 그룹 찾기
        const continuousGroups = this.findContinuousCellGroups(rowCells);

        continuousGroups.forEach((group) => {
          if (group.length <= 1) return;

          // 전체 너비 계산 (퍼센트 기반)
          let totalPercentage = 0;
          group.forEach((cell) => {
            // 현재 너비를 퍼센트로 변환
            let currentWidth = 0;

            // 1. style.width가 퍼센트인 경우
            const widthStyle = cell.style.width;
            if (widthStyle && widthStyle.includes("%")) {
              currentWidth = parseFloat(widthStyle);
            }
            // 2. style.width가 픽셀인 경우 - 테이블 전체 너비 대비 퍼센트로 변환
            else if (widthStyle && widthStyle.includes("px")) {
              const pixelWidth = parseInt(widthStyle);
              const table = cell.closest("table");
              const tableWidth = table.offsetWidth;
              currentWidth = (pixelWidth / tableWidth) * 100;
            }
            // 3. 기본값 (테이블 너비 대비 균등 분배)
            else {
              const table = cell.closest("table");
              const tableWidth = table.offsetWidth;
              const cellWidth = cell.offsetWidth;
              currentWidth = (cellWidth / tableWidth) * 100;
            }

            totalPercentage += currentWidth;
          });

          // 균등 퍼센트 계산
          const equalPercentage = totalPercentage / group.length;

          // 각 셀에 균등 퍼센트 적용
          group.forEach((cell) => {
            cell.style.width = equalPercentage + "%";
            cell.style.minWidth = equalPercentage + "%";
            cell.style.maxWidth = equalPercentage + "%";
          });
        });
      });

      editor.events.fire("afterResize");
    },

    // 선택된 셀들의 행 균등 분배
    distributeSelectedCellsHeight(editor) {
      // 먼저 우리가 직접 관리하는 선택된 셀들 확인
      let selectedCells = editor.__selectedCells || [];

      // 직접 관리하는 셀들이 없는 경우 다른 방법들 시도
      if (selectedCells.length === 0) {
        selectedCells = this.getSelectedTableCellsJodit(editor);
      }

      if (selectedCells.length === 0) {
        selectedCells = this.getSelectedTableCellsSimple(editor);
      }

      if (selectedCells.length === 0) {
        selectedCells = this.getSelectedTableCells(editor);
      }

      if (selectedCells.length === 0) {
        editor.alert("표에서 셀을 선택한 후 다시 시도해주세요.");
        return;
      }

      // 선택된 셀들을 열별로 그룹화
      const cellsByColumn = {};
      selectedCells.forEach((cell) => {
        const table = cell.closest("table");
        const row = cell.parentNode; // tr 요소
        const columnIndex = Array.from(row.children).indexOf(cell); // 열 인덱스

        if (!cellsByColumn[columnIndex]) {
          cellsByColumn[columnIndex] = [];
        }
        cellsByColumn[columnIndex].push(cell);
      });

      // 각 열에서 연속된 셀들의 높이 균등 분배
      Object.values(cellsByColumn).forEach((columnCells, columnIndex) => {
        if (columnCells.length <= 1) {
          return;
        }

        // 연속된 셀 그룹 찾기 (행 균등 분배용)
        const continuousGroups = this.findContinuousRowGroups(columnCells);

        continuousGroups.forEach((group, groupIndex) => {
          if (group.length <= 1) {
            return;
          }

          // 전체 높이 계산
          let totalHeight = 0;
          group.forEach((cell) => {
            // 현재 높이를 픽셀로 변환
            let currentHeight = 0;

            // 1. style.height가 픽셀인 경우
            const heightStyle = cell.style.height;
            if (heightStyle && heightStyle.includes("px")) {
              currentHeight = parseInt(heightStyle);
            }
            // 2. 기본값 (실제 렌더링된 높이)
            else {
              currentHeight = cell.offsetHeight || 30;
            }

            totalHeight += currentHeight;
          });

          // 균등 높이 계산
          const equalHeight = Math.round(totalHeight / group.length);

          // 각 셀에 균등 높이 적용
          group.forEach((cell) => {
            cell.style.height = equalHeight + "px";
            cell.style.minHeight = equalHeight + "px";
            cell.style.maxHeight = equalHeight + "px";
          });
        });
      });

      editor.events.fire("afterResize");
    },

    // 연속된 셀 그룹 찾기
    findContinuousCellGroups(cells) {
      if (cells.length === 0) return [];

      // 셀들을 부모 요소별로 정렬
      const sortedCells = [...cells].sort((a, b) => {
        const aParent = a.parentNode;
        const bParent = b.parentNode;

        if (aParent === bParent) {
          // 같은 행/열 내에서 위치 순서로 정렬
          const siblings = Array.from(aParent.children);
          return siblings.indexOf(a) - siblings.indexOf(b);
        }

        // 부모 요소의 순서로 정렬
        const allParents = Array.from(aParent.parentNode.children);
        return allParents.indexOf(aParent) - allParents.indexOf(bParent);
      });

      const groups = [];
      let currentGroup = [sortedCells[0]];

      for (let i = 1; i < sortedCells.length; i++) {
        const currentCell = sortedCells[i];
        const previousCell = sortedCells[i - 1];

        // 연속성 확인 (같은 부모에서 인접한 위치)
        const isContinuous = this.areCellsContinuous(previousCell, currentCell);

        if (isContinuous) {
          currentGroup.push(currentCell);
        } else {
          groups.push([...currentGroup]);
          currentGroup = [currentCell];
        }
      }

      groups.push(currentGroup);
      return groups;
    },

    // 두 셀이 연속되는지 확인
    areCellsContinuous(cell1, cell2) {
      const parent1 = cell1.parentNode;
      const parent2 = cell2.parentNode;

      // 같은 부모 요소인지 확인
      if (parent1 !== parent2) {
        // 다른 부모인 경우, 인접한 부모인지 확인
        const allParents = Array.from(parent1.parentNode.children);
        const index1 = allParents.indexOf(parent1);
        const index2 = allParents.indexOf(parent2);

        if (Math.abs(index1 - index2) === 1) {
          // 인접한 부모에서 첫 번째/마지막 셀인지 확인
          const siblings1 = Array.from(parent1.children);
          const siblings2 = Array.from(parent2.children);

          const cell1Index = siblings1.indexOf(cell1);
          const cell2Index = siblings2.indexOf(cell2);

          const isCell1Last = cell1Index === siblings1.length - 1;
          const isCell2First = cell2Index === 0;

          return isCell1Last && isCell2First;
        }
        return false;
      }

      // 같은 부모에서 인접한 위치인지 확인
      const siblings = Array.from(parent1.children);
      const index1 = siblings.indexOf(cell1);
      const index2 = siblings.indexOf(cell2);

      return Math.abs(index1 - index2) === 1;
    },

    // 행 균등 분배를 위한 연속된 행 그룹 찾기
    findContinuousRowGroups(cells) {
      if (cells.length === 0) return [];

      // 셀들을 행 순서대로 정렬 (부모 tr 요소의 순서)
      const sortedCells = [...cells].sort((a, b) => {
        const aParent = a.parentNode; // tr 요소
        const bParent = b.parentNode; // tr 요소

        // 같은 테이블 내에서 행 순서 비교
        const table = aParent.closest("table");
        const allRows = Array.from(table.querySelectorAll("tr"));
        const aRowIndex = allRows.indexOf(aParent);
        const bRowIndex = allRows.indexOf(bParent);

        return aRowIndex - bRowIndex;
      });

      const groups = [];
      let currentGroup = [sortedCells[0]];

      for (let i = 1; i < sortedCells.length; i++) {
        const currentCell = sortedCells[i];
        const previousCell = sortedCells[i - 1];

        // 연속된 행인지 확인
        const isContinuousRow = this.areCellsInContinuousRows(
          previousCell,
          currentCell
        );

        if (isContinuousRow) {
          currentGroup.push(currentCell);
        } else {
          groups.push([...currentGroup]);
          currentGroup = [currentCell];
        }
      }

      groups.push(currentGroup);
      return groups;
    },

    // 두 셀이 연속된 행에 있는지 확인
    areCellsInContinuousRows(cell1, cell2) {
      const row1 = cell1.parentNode; // tr 요소
      const row2 = cell2.parentNode; // tr 요소

      // 같은 행이면 연속됨
      if (row1 === row2) {
        return true;
      }

      // 다른 행인 경우, 인접한 행인지 확인
      const table = row1.closest("table");
      const allRows = Array.from(table.querySelectorAll("tr"));
      const row1Index = allRows.indexOf(row1);
      const row2Index = allRows.indexOf(row2);

      // 인접한 행인지 확인 (행 인덱스 차이가 1)
      return Math.abs(row1Index - row2Index) === 1;
    },
  },
  watch: {
    value(newValue) {
      if (newValue !== this.content) {
        this.content = newValue || "";
        // rawTextValue가 없고 value만 변경되는 경우, rawText도 업데이트
        if (!this.rawTextValue && newValue && newValue.includes('<')) {
          this.rawText = this.htmlToTextFormat(newValue);
        }
      }
    },
    rawTextValue(newValue) {
      if (newValue !== this.rawText) {
        this.rawText = newValue || "";
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.editor-mode-toggle {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
}

.mode-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border: 1px solid #ddd;
  background: #fff;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  transition: all 0.2s;

  &:hover {
    background: #f5f5f5;
    border-color: #ccc;
  }

  &.active {
    background: #2563eb;
    color: #fff;
    border-color: #2563eb;
  }
}

.html-editor-container {
  border: 1px solid #ddd;
  border-radius: 6px;
  overflow: hidden;
}

.html-editor {
  width: 100%;
  min-height: 500px;
  padding: 16px;
  font-family: "Consolas", "Monaco", "Courier New", monospace;
  font-size: 13px;
  line-height: 1.6;
  border: none;
  outline: none;
  resize: vertical;
  background: #1e1e1e;
  color: #d4d4d4;
  tab-size: 2;
}

.footnote-list-area {
  margin-top: 16px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background-color: #fafafa;
  overflow: hidden;
}

.footnote-list-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background-color: #f5f5f5;
  border-bottom: 1px solid #e0e0e0;
}

.footnote-list-title {
  font-size: 14px;
  font-weight: 600;
  color: #333;
}

.footnote-count {
  font-size: 12px;
  color: #666;
}

.footnote-list-content {
  padding: 12px 16px;
  max-height: 200px;
  overflow-y: auto;
}

.footnote-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px 0;
  border-bottom: 1px dashed #e0e0e0;
  line-height: 1.5;

  &:last-child {
    border-bottom: none;
  }
}

.footnote-number {
  color: #1976d2;
  font-weight: bold;
  min-width: 16px;
}

.footnote-text {
  font-size: 13px;
  color: #333;
  flex: 1;
}

.footnote-page {
  font-size: 11px;
  color: #888;
  margin-left: 4px;
}

.footnote-empty {
  text-align: center;
  color: #999;
  font-size: 13px;
  padding: 16px 0;
}
</style>
